import React from 'react';
import { StripeCardElement } from '@stripe/stripe-js';
import { sprintf } from 'sprintf';
import { motion, AnimatePresence } from 'framer-motion';

import { Modal } from 'components/modal';
import { Spinner } from 'components/Spinner';
import { formatPrice } from 'services/stringFormating';

import { CreditCardForm, CreditCardFormStates } from './CreditCardForm';
import { ConfirmForm, ConfirmFormStates } from './ConfirmForm';
import { ErrorForm } from './ErrorForm';
import { stateToContent } from './helpers';

export type BidModalStates = CreditCardFormStates | ConfirmFormStates | 'loading' | 'error';

type Props = {
  state: BidModalStates;
  isOpen: boolean;
  priceCents: number;
  errorMessage?: string;
  artworkTitle: string;
  creditCardLast4?: string;
  onSubmitCreditCard: (cardElement: StripeCardElement) => void;
  onSubmitBid: (priceCents: number) => void;
  onRequestClose: () => void;
  onRequestChangeCard: (last4: string) => void;
};

export const BidModal: React.FC<Props> = ({
  state,
  isOpen,
  priceCents,
  artworkTitle,
  errorMessage,
  creditCardLast4,
  onSubmitCreditCard,
  onSubmitBid,
  onRequestClose,
  onRequestChangeCard,
}) => {
  const content = stateToContent(state);

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose}>
      <h1 className='modal-title mb-1'>
        {sprintf('Bid %s on %s', formatPrice(priceCents, 'USD'), artworkTitle)}
      </h1>
      <AnimatePresence initial={false} exitBeforeEnter>
        <motion.div
          key={content}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {content === 'loading' ? (
            <div
              className='d-flex align-items-center justify-content-center h-100 w-100'
              style={{ minHeight: 100 }}
            >
              <Spinner />
            </div>
          ) : null}
          {content === 'confirm' ? (
            <ConfirmForm
              state={state as ConfirmFormStates}
              priceCents={priceCents}
              creditCardLast4={creditCardLast4}
              onSubmit={onSubmitBid}
              onCancel={onRequestClose}
              onRequestChangeCard={onRequestChangeCard}
            />
          ) : null}
          {content === 'credit-card' ? (
            <CreditCardForm
              state={state as CreditCardFormStates}
              priceCents={priceCents}
              onSubmit={onSubmitCreditCard}
              onCancel={onRequestClose}
            />
          ) : null}
          {content === 'error' ? (
            <ErrorForm
              errorMessage={errorMessage || 'Error has occurred. Please try again.'}
              onClose={onRequestClose}
            />
          ) : null}
        </motion.div>
      </AnimatePresence>
      {/* We're showing inline error only if we are not on a 'error' screen.
      When we are on 'error' screen we show the error fullscreen */}
      {state !== 'error' && errorMessage && errorMessage.length > 0 ? (
        <p className='text-center text-error mt-1 mb-0'>{errorMessage}</p>
      ) : null}
    </Modal>
  );
};
